import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer, faUsers, faCircle, faCode } from '@fortawesome/free-solid-svg-icons';

const StatusCard = styled.div`
  background: rgba(10, 10, 10, 0.8);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: var(--primary-color);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const StatusIcon = styled.div`
  font-size: 1.5rem;
  color: var(--primary-color);
  margin-right: 1rem;
`;

const StatusTitle = styled.h3`
  color: #fff;
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
`;

const ServerIP = styled.div`
  background-color: rgba(20, 20, 20, 0.8);
  border-radius: 4px;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  min-width: 300px;
`;

const StatusInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  gap: 2rem;
`;

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  background: rgba(255, 51, 51, 0.1);
  padding: 1.2rem 2rem;
  border-radius: 12px;
  text-align: left;
  border: 1px solid rgba(255, 51, 51, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    border-color: var(--primary-color);
    box-shadow: 0 8px 20px rgba(255, 51, 51, 0.2);
  }
`;

const StatusLabel = styled.span`
  color: #aaa;
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const StatusValue = styled.span`
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: var(--primary-color);
  }
`;

const IPText = styled.span`
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
`;

const CopyButton = styled.button`
  background-color: var(--primary-color);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--accent-color);
  }
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
`;

const StatusDot = styled.div`
  color: ${props => props.online ? '#4CAF50' : '#F44336'};
  margin-right: 0.5rem;
  font-size: 0.8rem;
`;

const StatusText = styled.span`
  color: ${props => props.online ? '#4CAF50' : '#F44336'};
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
`;

const ServerStatus = ({ serverIP }) => {
  const [status] = useState({
    online: true,
    players: {
      online: 42,
      max: 100
    },
    version: '1.19.2'
  });
  
  const [copied, setCopied] = useState(false);
  
  // In a real implementation, you would fetch the server status from an API
  // For this example, we're using mock data
  
  const copyIP = () => {
    navigator.clipboard.writeText(serverIP);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <StatusCard>
      <StatusHeader>
        <StatusIcon>
          <FontAwesomeIcon icon={faServer} />
        </StatusIcon>
        <StatusTitle>Server Status</StatusTitle>
      </StatusHeader>
      
      <ServerIP>
        <IPText>{serverIP}</IPText>
        <CopyButton onClick={copyIP}>
          {copied ? 'Copied!' : 'Copy IP'}
        </CopyButton>
      </ServerIP>
      
      <StatusInfo>
  <StatusItem>
    <StatusLabel>Players</StatusLabel>
    <StatusValue>
      <FontAwesomeIcon icon={faUsers} />
      {status.players.online}/{status.players.max}
    </StatusValue>
  </StatusItem>
  <StatusItem>
    <StatusLabel>Version</StatusLabel>
    <StatusValue>
      <FontAwesomeIcon icon={faCode} />
      {status.version}
    </StatusValue>
  </StatusItem>
</StatusInfo>
      
      <StatusIndicator>
        <StatusDot online={status.online}>
          <FontAwesomeIcon icon={faCircle} />
        </StatusDot>
        <StatusText online={status.online}>
          {status.online ? 'Server Online' : 'Server Offline'}
        </StatusText>
      </StatusIndicator>
    </StatusCard>
  );
};

export default ServerStatus;
